/**
 * Percent solutions: % w/v, % w/w and % v/v.
 *
 *   % w/v  g of solute per 100 mL of solution
 *   % w/w  g of solute per 100 g of solution
 *   % v/v  mL of solute per 100 mL of solution
 *
 *   percent = solute ÷ solution × 100
 *
 * All amounts here are in the kind's base units: grams or millilitres. The percent is the
 * share of the *finished solution*, never of the solvent alone, so 5% w/w is 5 g of solute
 * in 100 g of solution (95 g of solvent), not 5 g in 100 g of solvent.
 */

import type { PercentKind } from "@/lib/tool-kit/chemistry/solutions";

export type PercentSolve = "solute" | "percent" | "solution";

export interface PercentInput {
  solve: PercentSolve;
  percent: number | null;
  /** g (w/v, w/w) or mL (v/v) */
  solute: number | null;
  /** mL (w/v, v/v) or g (w/w) */
  solution: number | null;
}

export interface PercentResult {
  percent: number;
  solute: number;
  solution: number;
  /** Solution − solute: grams of solvent (w/w) or mL to add (v/v). Null for w/v, which is made up to volume. */
  solvent: number | null;
}

/** The three quantities from any two, or null when an input is missing or not positive. */
export function solvePercent(
  kind: PercentKind,
  input: PercentInput
): PercentResult | null {
  const { solve } = input;
  const usable = (value: number | null) =>
    value !== null && Number.isFinite(value) && value > 0;
  let { percent, solute, solution } = input;
  if (solve !== "percent" && !usable(percent)) {
    return null;
  }
  if (solve !== "solute" && !usable(solute)) {
    return null;
  }
  if (solve !== "solution" && !usable(solution)) {
    return null;
  }
  if (solve === "percent") {
    percent = ((solute as number) / (solution as number)) * 100;
  } else if (solve === "solute") {
    solute = ((percent as number) / 100) * (solution as number);
  } else {
    solution = ((solute as number) / (percent as number)) * 100;
  }
  const p = percent as number;
  const sol = solute as number;
  const total = solution as number;
  return {
    percent: p,
    solute: sol,
    solution: total,
    solvent: kind === "wv" ? null : total - sol,
  };
}

/**
 * Molarity of a percent solution, mol/L.
 *
 * - w/v: percent × 10 g/L of solute, divided by the molar mass.
 * - w/w: needs the solution's density (g/mL): g/L = percent × 10 × density.
 * - v/v: needs the pure solute's density (g/mL): g/L = percent × 10 × density.
 *
 * Null when the molar mass is not positive, or a density is missing where one is needed.
 */
export function percentToMolarity(input: {
  kind: PercentKind;
  percent: number;
  molarMass: number;
  density: number | null;
}): number | null {
  const { kind, percent, molarMass, density } = input;
  if (!(percent > 0 && molarMass > 0)) {
    return null;
  }
  if (kind === "wv") {
    return (percent * 10) / molarMass;
  }
  if (density === null || !(density > 0)) {
    return null;
  }
  return (percent * 10 * density) / molarMass;
}

/** Grams of solute per litre of solution (g/L), the bridge between a percent and a molarity. */
export function gramsPerLitre(input: {
  kind: PercentKind;
  percent: number;
  density: number | null;
}): number | null {
  const { kind, percent, density } = input;
  if (kind === "wv") {
    return percent * 10;
  }
  if (density === null || !(density > 0)) {
    return null;
  }
  return percent * 10 * density;
}
